import type { CancelSubscriptionPayload } from '@/features/founders/api/founders';
import api from '@/lib/axios';

export interface AdminLicensee {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  createdAt: string;
  updatedAt?: string;
  country: string;
  mentor?: AdminMentor | string | null;
  cohort: AdminCohort | string;
  nonAIFounder?: boolean; // Legacy tracking only
  subscriptionActive: boolean;
  // STA-743: populated when the founder (or admin) has clicked Cancel.
  // While subscriptionActive is still true, the daily cron hasn't run yet.
  // Used to disable the Cancel Subscription action so we don't re-fire
  // the persistence + Slack alert.
  subscriptionCancellationRequestedAt?: string;
  subscriptionEndsAt?: string;
  stripeActive: boolean;
  level?: string;
  auth0Id?: string;
  onboarded?: boolean;
  isDownpayment?: boolean;
  isDownpaymentPaid?: boolean;
  hasSharedDemo?: boolean;
  hasCompletedPlaybook?: boolean;
  hasLandingPage?: boolean;
  leadFinderEnabled?: boolean;
  subscriptionValidUntil?: string;
  // New fields from API response
  hash?: string;
  stripeCustomerId?: string;
  stripeId?: string;
  stripeSubscriptionId?: string;
  welcomeEmailSent?: boolean;
  accountActivated?: boolean;
  referralCode?: string;
  businessCategory?: string[];
  nicheWhat?: string;
  nicheWho?: string;
  marketplaceProfile?: {
    _id: string;
  } | null;
  applicant?: {
    linkedinUrl?: string;
  } | null;
}

export interface AdminMentor {
  _id: string;
  firstName: string;
  lastName: string;
  // Optional fields that may be present in expanded expert payloads
  email?: string;
  auth0Id?: string;
  createdAt?: string;
  updatedAt?: string;
  bio?: string;
  calendarLink?: string;
  referralCode?: string;
}

export interface AdminCohort {
  _id: string;
  number: number;
  startDate: string;
}

export enum AdminFoundersKeys {
  GET_FOUNDERS = 'admin-get-founders',
  GET_FOUNDER = 'admin-get-founder',
  ASSIGN_MENTOR = 'admin-assign-mentor',
}

export const createAdminFounder = async (payload: Partial<AdminLicensee>) => {
  const response = await api.post('/licensees', payload);
  return response.data as { user: AdminLicensee };
};

export interface AdminFoundersResponse {
  data: AdminLicensee[];
  total: number;
  skip: number;
  limit: number;
}

export const getAdminFounders = async (
  search?: string,
  sortBy?: string,
  sortOrder?: 'asc' | 'desc',
  skip?: number,
  limit?: number,
  subscriptionActive?: boolean,
  level?: string,
) => {
  const params: any = {};
  if (search) params.search = search;
  if (sortBy) params.sortBy = sortBy;
  if (sortOrder) params.sortOrder = sortOrder;
  if (skip !== undefined) params.skip = skip.toString();
  if (limit !== undefined) params.limit = limit.toString();
  if (subscriptionActive !== undefined)
    params.subscriptionActive = subscriptionActive.toString();
  if (level) params.level = level;

  const response = await api.get('/licensees', { params });

  if (response.data && 'data' in response.data) {
    return {
      items: response.data.data as AdminLicensee[],
      total: response.data.total,
      skip: response.data.skip,
      limit: response.data.limit,
    };
  }

  if (Array.isArray(response.data)) {
    return {
      items: response.data as AdminLicensee[],
      total: response.data.length,
      skip: skip || 0,
      limit: limit || response.data.length,
    };
  }

  return {
    items: [],
    total: 0,
    skip: skip || 0,
    limit: limit || 10,
  };
};

export const getAdminFounder = async (id: string) => {
  const response = await api.get(`/licensees/${id}`);
  return response.data as AdminLicensee;
};

export const updateAdminFounder = async (
  id: string,
  updatedFounder: Partial<AdminLicensee>,
) => {
  const response = await api.patch(`/licensees/${id}`, updatedFounder);
  return response.data as AdminLicensee;
};

export const sendAdminFounderStripeReminder = async (id: string) => {
  const response = await api.post(
    `/licensees/${id}/send-stripe-onboarding-email`,
  );
  return response.data;
};

export const deleteAdminFounder = async (id: string) => {
  const response = await api.delete(`/licensees/${id}`);
  return response.data;
};

// progression update moved to levels API

export interface AssignMentorDto {
  licenseeId: string;
  mentorId: string;
}

export const assignMentor = async (payload: AssignMentorDto) => {
  const response = await api.post(`/licensees/assign-mentor`, payload);
  return response.data;
};

// levels progression moved to ./levels.ts

// STA-743 — admin row actions on the founders table.
//
// `adminCancelSubscription` mirrors the founder-side cancellation flow but
// records the admin's auth0Id on the licensee and tags the Slack message.
// `adminOffboardNow` skips the cron wait and runs all four offboarding
// steps now (Circle → Auth0 → flip subscriptionActive → Attio). It must be
// gated behind a typed-name confirm dialog client-side.

export const adminCancelSubscription = async (
  id: string,
  payload: CancelSubscriptionPayload,
) => {
  const response = await api.post(
    `/licensees/${id}/admin-cancel-subscription`,
    payload,
  );
  return response.data;
};

export const adminOffboardNow = async (id: string) => {
  const response = await api.post(`/licensees/${id}/admin-offboard-now`);
  return response.data;
};
